import { createContext } from '@fluentui/react-context-selector';
import type {
  IconGlyph,
  IconStyle,
} from '@ipfabric/ui-components/Icon/IconGlyph';
import type {
  FloatingListDividerProps,
  FloatingListItemProps,
} from '@ipfabric/ui-components/List/FloatingList/FloatingList.types';
import type { LoadingPhase } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import {
  type FilterPreset,
  type Point,
  type ReportData,
  type ReportSeverityKey,
  type TableColumn,
  type TableColumnSort,
  type TableSettings,
  makeTypeGuardByPropertyValue,
} from '@ipfabric/utils';
import type { ISize } from '@ipfabric/utils/elements/types';
import type { ButtonAccent } from 'components/Button/Button';
import type { RequestRowRangeEventHandler } from 'components/LazyRowContainer/LazyRowContainer';
import type {
  ChangeFilterEvent,
  ChangeIntentFilterEvent,
} from 'components/Table/components/SimpleFilter/types/ChangeFilterEvent';
import type { SimpleFilterField } from 'components/Table/components/SimpleFilter/types/SimpleFilterField';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import type { ReactNode, RefObject } from 'react';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';
import type { ApiHook } from 'utils/request/useApi';
import type { Falsy } from 'utils/types/Falsy';

export const AUTO_SIZED_COLUMNS_DEFAULT = true;

export type TableReports = Record<string, ReportData>;
export type IntentFilters = Record<string, ReportSeverityKey | null>;

/**
 * Possible column value types to be used as rowIdField
 */
export type TableRowIdValue = string | number;

/**
 * Load row ids for "select all" feature
 *
 * That means, row ids that conform to current filter and can be selected.
 */
export type LoadRowIdsForSelectAllFunction = (
  /**
   * For deselect operation we need to load row ids only when filter is present,
   * otherwise we just deselect everything. So, when `true` is passed and no filters
   * are active, return `null` to indicate that Table can just deselect everything.
   */
  omitWhenFilterIsNotPresent?: boolean,
) => Awaitable<TableRowIdValue[] | null>;

export type LoadRowIdsForSelectAll =
  | LoadRowIdsForSelectAllFunction
  /** Use for tables that have all data available all the time -> we can just browse data to load ids */
  | 'fromData';

export type TableContextMenu = {
  position: Point;
  index: number;
};

export type TableRowDetail<TRow> = {
  row: TRow;
  columns: TableColumn<any>[];
  expandedColumns: Set<string>;
};

/**
 * Represents range of rows rendered in viewport (startIndex and endIndex inclusive)
 */
export type TableRowRange = {
  startIndex: number;
  endIndex: number;
};

export type TableSelectionState = {
  selectedRows: Set<TableRowIdValue>;
};

export interface TableState {
  /**
   * Show "Select all rows" checkbox checked, making next click = deselect all.
   *
   * The state is updated only by clicking the component, it is independent
   * on interaction with individual rows.
   */
  allRowsSelectState: 'unchecked' | 'pending' | 'checked';
  contentHeight: number;
  /**
   * Increased via requestReload command to indicate that table content has changed and
   * make lazy row container re-request data.
   */
  dataRequestNumber: number;
  settings: TableSettings;
  loadingSettings: LoadingPhase;
  /**
   * Either a table without id and therefore no persistent settings or settings are initially loaded.
   *
   * E.g. fetch from backend should not happen before settings load first, because all columns
   * would be fetched instead of just visible ones.
   */
  loadedSettings: boolean;
  filterPresets: FilterPreset[];
  openSettings: boolean;
  /**
   * Whether to auto size columns.
   */
  requestedAutoSize: boolean;
  selectedRows: Set<TableRowIdValue>;
  tableContainer: HTMLDivElement | null;
  /**
   * clientWidth of the outer element containing the scrollable table
   */
  tableContainerSize: ISize | null;
  tableContextMenu: TableContextMenu | null;
  tableRowDetail: TableRowDetail<unknown> | null;
  visibleRowRange: null | TableRowRange;
}

export interface TableAdditionalCommandContext {
  api: ApiHook;
  onRestoreSettings?: () => void;
}

export type TableStandaloneActionItem = {
  // currently custom element is supported only because it is the only use case,
  // if we add "standardizable" buttons in the future, let's add more fields
  // to this interface and make element optional.
  // Don't forget to provide a key! elements are rendered in a loop.
  elementWithKey: ReactNode;
  standalone: true;
};

export const isTableStandaloneActionItem = makeTypeGuardByPropertyValue<
  TableActionsItem,
  TableStandaloneActionItem
>()('standalone', true);

export const isTableActionDropdownMenuItem = (
  tableActionsItem: TableActionsItem,
): tableActionsItem is FloatingListItemProps =>
  !!tableActionsItem && !isTableStandaloneActionItem(tableActionsItem);

export type TableActionsItem =
  | FloatingListItemProps
  | FloatingListDividerProps
  | TableStandaloneActionItem
  | Falsy;

export interface TableBulkActionClickEvent {
  selectedRows: Set<TableRowIdValue>;
  selectedData: unknown[];
  deselectAll: () => void;
  indicateRunningAction: (callback: () => Awaitable<void>) => Promise<void>;
}

type BaseTableBulkActionProps = {
  accent?: ButtonAccent;
  disabled?: boolean;
  /**
   * Reason why the action is disabled, show as a tooltip
   */
  disabledReason?: string;
  /**
   * Do not deselect items after bulk action is triggered
   *
   * This is opt-out to encourage good UX: items should be
   * deselected either always or in some specific controlled
   * way (confirming in some), but it should not be forgot
   * to be handled.
   */
  disableAutoDeselect?: boolean;
  icon?: IconGlyph;
  iconStyle?: IconStyle;
  title: string;
  /** React button element key */
  key: string;
};

type ButtonTableBulkActionProps = BaseTableBulkActionProps & {
  onClick: (event: TableBulkActionClickEvent) => Awaitable<void>;
};

type LinkTableBulkActionProps = BaseTableBulkActionProps & {
  href: string;
};

export type TableBulkActionProps =
  | ButtonTableBulkActionProps
  | LinkTableBulkActionProps;

export type TableBulkAction =
  | TableBulkActionProps
  | ((state: Pick<TableState, 'selectedRows'>) => TableBulkActionProps);

/**
 * Filters to be listed in AppliedFilters overview not managed by the Table component itself.
 *
 * At the time of writing, only Advanced Filter uses this.
 */
export type TableExternalFilter = {
  key: string;
  label: ReactNode;
};

/**
 * Props passed from Table/useTable to outer state
 *
 * Pass through all props here in Table.tsx and useTable.tsx to the outer state.
 */
export interface TableOuterStateProps<TRow> {
  /**
   * Unique identifier of table settings
   *
   * If not present, table settings will not be saved.
   */
  id?: string;
  bulkActions?: TableBulkAction[];
  /**
   * If table has row selection active, rows for which this function returns false will
   * have disabled checkbox.
   *
   * Don't forget to reflect the same condition in provided `loadSelectAllRowIds` function.
   */
  canRowBeSelected?: (row: unknown, state?: TableSelectionState) => boolean;
  disableSelectAll?: boolean;
  externalFilters?: TableExternalFilter[];
  /**
   * Remove active filters
   *
   * Remove all filters if no argument is present, or only specified ones.
   * If we need to remove only some filters in the future, extend support via arguments of this function.
   */
  onRemoveFilters?: (args?: {
    externalFilterKeys?: string[];
  }) => void;

  columns: TableColumn<any>[];
  data: TRow[];
  description?: ReactNode;
  /**
   * Override table settings
   *
   * - not passed (undefined):
   *   - if table has `id` prop, it loads settings from API, changes to settings are saved to API
   *   - else it uses default settings without saving
   * - passed explicit settings: override those saved for the table, do not save changes in settings
   *
   * Note: even when external table settings are passed, settings endpoint is requested
   * to load filter presets.
   */
  externalSettings?: TableSettings;
  initialSelectedRows?: TableRowIdValue[];
  intentFilters?: IntentFilters;
  /**
   * Explicitly render loading indicator instead of content
   *
   * Useful when an external operation is running, e.g. deleting a row.
   */
  loading?: boolean;
  loadRowIdsForSelectAll?: LoadRowIdsForSelectAll;
  menuItems?: TableActionsItem[];
  onChangeFilter?: (command: ChangeFilterEvent) => void;
  onChangeIntentFilter?: (command: ChangeIntentFilterEvent) => void;
  /** @see LazyRowContainer */
  onRequestRowRange?: RequestRowRangeEventHandler;
  onSubmitFilters?: () => void;
  reports?: TableReports;
  /**
   * Used for lazy loaded data into tables
   *
   * number: actual total number of rows in the table
   * null: actual total number is unknown (see LazyRowContainer)
   * undefined/omitted: no lazy loading, use `data.length`
   */
  rowCount?: number | null;
  /**
   * For some tables we don't know the row count. For those we provide fake `rowCount` to make lazy row
   * container work and set this flag to true so UI can indicate there may be more rows.
   */
  rowCountMore?: boolean;
  /** Needed only to enable features like row selection, not needed for all tables */
  rowIdField?: string;
  selectedFilterPreset?: null | FilterPreset;
  onSelectFilterPreset?: (presetId: string | null) => void;
  showOnlyIntentResultsOnColumns?: string[];
  simpleFilterFields?: Record<string, SimpleFilterField>;
  simpleFilterFieldsSubmitted?: Record<string, SimpleFilterField>;
  sort?: TableColumnSort;
  onSortChange?: (order: TableColumnSort) => void;
  title?: string;
  titleEndAdornment?: ReactNode;
  disableRestoreSettings?: boolean;
  disableContextMenu?: boolean;
  disableStickyColumn?: boolean;
  equalWidthColumns?: boolean;
  hideAutoSizing?: boolean;
  tableContainerRef?: RefObject<HTMLDivElement>;
  snapshotId?: string | null;
  noDataBox?: ReactNode;
}

export type TableOuterState = TableOuterStateProps<unknown> & {
  tableCellContext: TableCellContext;
};

type S = TableState;
type C = TableAdditionalCommandContext;
type O = TableOuterState;

export type TableCommandContext = CommandModelCommandContext<S, C, O>;
export type TableCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<S, C, O, TCommandArgs>;
export type TableDispatch = CommandModelCommandDispatcher<S, C, O>;
export type TableMergedState = CommandModelMergedState<S, O>;
export type TableModel = CommandModel<S, C, O>;

export const createTableInitialState = (
  id?: string,
  externalSettings?: TableSettings,
  initialSelectedRows?: TableRowIdValue[],
): TableState => ({
  allRowsSelectState: 'unchecked',
  contentHeight: 0,
  dataRequestNumber: 0,
  filterPresets: [],
  loadingSettings: id === undefined ? 'LOADED' : 'LOADING',
  loadedSettings: id === undefined,
  openSettings: false,
  requestedAutoSize: true,
  selectedRows: new Set(initialSelectedRows),
  settings: externalSettings ?? {},
  tableContainer: null,
  tableContainerSize: null,
  tableContextMenu: null,
  tableRowDetail: null,
  visibleRowRange: null,
});

export const TableContext = createContext<TableModel>(undefined!);

export const useTableContextSelectors: ContextSelectorsHook<TableModel> = (
  selectors,
) => useContextSelectors(TableContext, selectors);
